import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Select, Button, Table, Tag } from "antd";
import Layout from "../../Layout";

interface StaffStatusItem {
    key: string;
    code: string;
    statusText: string;
    statusBg: string;
    duration: string;
    overdueTasks: number;
    canMonitor: boolean;
}

interface SeverityTaskItem {
    key: string;
    type: string;
    typeColor: string;
    openCount: number;
    overdueCount: number;
}

interface HandoverItem {
    key: string;
    direction: string;
    customerCount: number | string;
    waitingReceive: number | string;
    overdueReceive: number | string;
}

export default function TongQuanCSKHPage() {
    const { t } = useTranslation();

    // State quản lý trạng thái nhân viên CSKH ở góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // Dữ liệu bảng Trạng thái nhân viên CSKH
    const staffColumns = [
        {
            title: "Mã NV",
            dataIndex: "code",
            key: "code",
            width: 90,
            render: (text: string) => <span className="font-semibold text-gray-900 dark:text-gray-100">{text}</span>,
        },
        {
            title: "Trạng thái · lý do",
            dataIndex: "statusText",
            key: "statusText",
            render: (text: string, record: StaffStatusItem) => (
                <Tag className={`m-0 text-xs px-2 py-0.5 border-0 rounded font-medium ${record.statusBg}`}>
                    {text}
                </Tag>
            ),
        },
        {
            title: "Trong trạng thái",
            dataIndex: "duration",
            key: "duration",
            width: 120,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>,
        },
        {
            title: "Việc quá hạn",
            dataIndex: "overdueTasks",
            key: "overdueTasks",
            width: 110,
            render: (num: number) => (
                <span className={`font-bold ${num > 0 ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-300"}`}>
                    {num}
                </span>
            ),
        },
        {
            title: "Giám sát",
            key: "monitor",
            width: 130,
            render: (_: any, record: StaffStatusItem) =>
                record.canMonitor ? (
                    <div className="flex items-center gap-1.5">
                        <Button size="small" className="text-xs h-6 px-2 border-gray-300 dark:border-gray-600">
                            Nghe
                        </Button>
                        <Button size="small" className="text-xs h-6 px-2 border-gray-300 dark:border-gray-600">
                            Nhắc
                        </Button>
                    </div>
                ) : (
                    <span className="text-gray-400">—</span>
                ),
        },
    ];

    const staffData: StaffStatusItem[] = [
        {
            key: "1",
            code: "NV-07",
            statusText: "Đang gọi",
            statusBg: "bg-blue-600 text-white",
            duration: "03:15",
            overdueTasks: 0,
            canMonitor: true,
        },
        {
            key: "2",
            code: "NV-08",
            statusText: "Tạm nghỉ · Đào tạo",
            statusBg: "bg-orange-500 text-white",
            duration: "25:10",
            overdueTasks: 2,
            canMonitor: false,
        },
    ];

    // Dữ liệu bảng Việc theo mức nghiêm trọng (Đã chỉnh chuẩn các nhãn màu theo hình mẫu)
    const severityColumns = [
        {
            title: "Loại",
            dataIndex: "type",
            key: "type",
            render: (text: string, record: SeverityTaskItem) => (
                <Tag className={`m-0 text-xs px-2.5 py-0.5 border-0 rounded font-bold ${record.typeColor}`}>
                    {text}
                </Tag>
            ),
        },
        {
            title: "Đang mở",
            dataIndex: "openCount",
            key: "openCount",
            width: 100,
            render: (num: number) => <span className="font-semibold text-gray-900 dark:text-gray-100">{num}</span>,
        },
        {
            title: "Quá hạn",
            dataIndex: "overdueCount",
            key: "overdueCount",
            width: 100,
            render: (num: number) => (
                <span className={`font-bold ${num > 0 ? "text-red-600 dark:text-red-400" : "text-gray-400"}`}>
                    {num > 0 ? num : "—"}
                </span>
            ),
        },
    ];

    const severityData: SeverityTaskItem[] = [
        { key: "1", type: "Biến chứng", typeColor: "!bg-[#b71c1c] !text-white", openCount: 1, overdueCount: 0 },
        { key: "2", type: "Khiếu nại", typeColor: "!bg-[#b35900] !text-white", openCount: 2, overdueCount: 1 },
        { key: "3", type: "T+24h", typeColor: "!bg-[#1d4c7d] !text-white", openCount: 5, overdueCount: 0 },
        { key: "4", type: "Nhắc lịch / thẻ sắp hết hạn", typeColor: "!bg-[#3E91AB] !text-white", openCount: 6, overdueCount: 1 },
        { key: "5", type: "Gọi lại khách cũ (A, C)", typeColor: "!bg-[#5b9bd5] !text-white", openCount: 14, overdueCount: 0 },
    ];

    // Dữ liệu bảng Chuyển giao trong tuần
    const handoverColumns = [
        {
            title: "Hướng",
            dataIndex: "direction",
            key: "direction",
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium">{text}</span>,
        },
        {
            title: "Số khách",
            dataIndex: "customerCount",
            key: "customerCount",
            width: 120,
            render: (val: any) => <span className="font-semibold text-gray-900 dark:text-gray-100">{val}</span>,
        },
        {
            title: "Chờ nhận",
            dataIndex: "waitingReceive",
            key: "waitingReceive",
            width: 120,
            render: (val: any) => <span className="text-gray-700 dark:text-gray-300">{val}</span>,
        },
        {
            title: "Quá hạn nhận [?]",
            dataIndex: "overdueReceive",
            key: "overdueReceive",
            width: 140,
            render: (val: any) => <span className="text-gray-700 dark:text-gray-300">{val}</span>,
        },
    ];

    const handoverData: HandoverItem[] = [
        { key: "1", direction: "Thanh toán lần đầu → danh sách CSKH, CS0 (R34)", customerCount: 12, waitingReceive: "—", overdueReceive: "—" },
        { key: "2", direction: "CSKH → Tư vấn cơ sở (R21)", customerCount: 3, waitingReceive: 1, overdueReceive: 0 },
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available":
                return "#52c41a";
            case "break":
                return "#d48806";
            case "offline":
                return "#8c8c8c";
            default:
                return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Tổng quan điều hành CSKH | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-sm flex flex-col justify-between transition-colors duration-200">
                <div className="space-y-4 pb-16">
                    
                    {/* Thanh Header Điều Hướng Top */}
                    <div className="flex flex-wrap items-center justify-between border-b border-gray-300 dark:border-gray-700 pb-2 gap-2">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 border-l-4 border-[#1d4c7d] pl-2">
                            <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-base cursor-pointer">
                                Tổng quan — điều hành + chỉ số CSKH
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                                Vai trò: Trưởng phòng CSKH
                            </span>
                            <span className="text-gray-600 dark:text-gray-400 hidden md:inline">
                                Quy tắc: Chỉ số mục 7 nhóm C1 · R23 doanh thu khách cũ qua CSKH (đã chốt 15/10/2026) · R11 · R21
                            </span>
                        </div>
                    </div>

                    {/* Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                    <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1.5 font-bold tracking-wider rounded">
                        DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                    </div>

                    {/* 6 Thẻ Thống Kê Tổng Quan Hàng Trên */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        
                        {/* Thẻ 1 */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">T+24h đúng hạn (tuần)</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">92%</div>
                            <div className="text-xs text-gray-500">Chuẩn SOP: 100%</div>
                        </div>

                        {/* Thẻ 2 */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">CSAT trung bình</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">4,4</div>
                            <div className="text-xs text-gray-500">Phiếu ≤ 3: 2</div>
                        </div>

                        {/* Thẻ 3 (Highlight Cam) */}
                        <div className="bg-white dark:bg-[#1f1f1f] border-2 border-orange-400 dark:border-orange-500 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Khiếu nại xử lý đúng hạn</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">3 / 4</div>
                            <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">1 phiếu đang mở quá 1 ngày</div>
                        </div>

                        {/* Thẻ 4 */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Tỷ lệ khách quay lại (90 ngày)</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">38%</div>
                            <div className="text-xs text-gray-500">Khách có dịch vụ kỳ trước</div>
                        </div>

                        {/* Thẻ 5 */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Doanh thu khách cũ qua CSKH (tuần)</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">186 tr</div>
                            <div className="text-xs text-gray-500">7 đơn mã CS-06 · R23</div>
                        </div>

                        {/* Thẻ 6 */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded p-4 shadow-sm space-y-1">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Gọi lại khách cũ A, C (tuần)</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">27</div>
                            <div className="text-xs text-gray-500">Chuẩn SOP: ≥ 30</div>
                        </div>

                    </div>

                    {/* BỐ CỤC 2 CỘT: TRẠNG THÁI NHÂN VIÊN & VIỆC THEO MỨC NGHIÊM TRỌNG */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                        
                        {/* Cột Trái: Trạng thái nhân viên CSKH */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-sm space-y-3">
                            <div className="font-bold text-gray-900 dark:text-gray-100 text-base">
                                Trạng thái nhân viên CSKH
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <Table
                                    columns={staffColumns}
                                    dataSource={staffData}
                                    pagination={false}
                                    size="middle"
                                    className="custom-waiting-table"
                                    scroll={{ x: "max-content" }}
                                />
                            </div>
                        </div>

                        {/* Cột Phải: Việc theo mức nghiêm trọng — hôm nay */}
                        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-sm space-y-3">
                            <div className="font-bold text-gray-900 dark:text-gray-100 text-base">
                                Việc theo mức nghiêm trọng — hôm nay
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <Table
                                    columns={severityColumns}
                                    dataSource={severityData}
                                    pagination={false}
                                    size="middle"
                                    className="custom-waiting-table"
                                    scroll={{ x: "max-content" }}
                                />
                            </div>
                        </div>

                    </div>

                    {/* PHẦN DƯỚI: CHUYỂN GIAO TRONG TUẦN (R20, R21) */}
                    <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-sm space-y-3">
                        <div className="font-bold text-gray-900 dark:text-gray-100 text-base">
                            Chuyển giao trong tuần (R20, R21)
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <Table
                                columns={handoverColumns}
                                dataSource={handoverData}
                                pagination={false}
                                size="middle"
                                className="custom-waiting-table"
                                scroll={{ x: "max-content" }}
                            />
                        </div>
                    </div>

                </div>

                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-40 mx-3 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}